import axios, { AxiosError } from 'axios'
import config from 'config'
import { ComponentColumnSortingDirection } from 'containers/SavedApplicationsList/enums'
import {
    FiltersPanelState,
    ComponentTableSortingState,
    ComponentColumnSortingState,
} from 'containers/SavedApplicationsList/types'
import ApplicationStatus from 'state/enums/ApplicationStatus'
import AssetType from 'state/enums/AssetType'
import getPortal from 'utils/getPortal'

import { OctaneLogger } from '../../integrations/datadog/OctaneLogger'
import { defaultLosJsonHeaders } from '../requestHeaders'

export const ALL_USERS = '__all__'

export const MAP_LOS_SPECIAL_FIELDS_NAMES = {
    primaryApplicant: 'name',
} as { [k: string]: string }

type FiltersConstraints = Partial<FiltersPanelState>

function isSpecialField(fieldName: string) {
    return Object.keys(MAP_LOS_SPECIAL_FIELDS_NAMES).includes(fieldName)
}

function buildSortingParamString(sorting?: ComponentTableSortingState): string {
    if (!sorting) return ''

    return sorting
        .filter(({ order }: ComponentColumnSortingState) => !!order)
        .map(({ field, order }: ComponentColumnSortingState) => {
            const losField = isSpecialField(field)
                ? MAP_LOS_SPECIAL_FIELDS_NAMES[field]
                : field

            return `${
                order === ComponentColumnSortingDirection.DESC ? '-' : ''
            }${losField}`
        })
        .join(',')
}

function buildUsersParam(users?: string[]): string {
    if (!users || users.length === 0) return ALL_USERS

    return users.join(',')
}

export function buildDealershipsParam(dealershipIds?: number[]): string {
    if (!dealershipIds || dealershipIds.length === 0) return ''

    return dealershipIds.join(',')
}

function buildFiltersParam(filters?: ApplicationStatus[]): string {
    if (!filters || filters.length === 0) return ''

    return filters.map((x) => x.replaceAll(' ', '_')).join(',')
}

function buildAssetTypesParam(assetTypes?: AssetType[]): string {
    if (!assetTypes || assetTypes.length === 0) return ''

    return assetTypes.join(',')
}

export default async function getSavedApplications(
    page: number,
    pageSize: number,
    sorting: ComponentTableSortingState,
    filtersConstraints: FiltersConstraints,
    signal: AbortSignal,
) {
    try {
        const { data } = await axios.get(
            `${config.API_BASE_URL}/api/v3/applications/`,
            {
                withCredentials: true,
                signal,
                params: {
                    page,
                    page_size: pageSize,
                    sorted_by: buildSortingParamString(sorting),
                    is_archived: filtersConstraints.showArchivedOnly ?? false,
                    filters: buildFiltersParam(filtersConstraints.filters),
                    user: buildUsersParam(filtersConstraints.owners),
                    search: filtersConstraints.searchQuery ?? '',
                    last_n_days: filtersConstraints.createdOnTheLastNDays ?? '',
                    asset_type: buildAssetTypesParam(
                        filtersConstraints.assetTypes,
                    ),
                    leads_filter: 'no-leads',
                    default_sorted_by: '-created,-id',
                    dealership_ids: buildDealershipsParam(
                        filtersConstraints.dealershipIds,
                    ),
                    portal: getPortal(),
                },
                headers: defaultLosJsonHeaders(),
            },
        )

        return data
    } catch (error) {
        const axiosError = error as AxiosError

        OctaneLogger.axiosError(
            `Failed to get saved applications: ${axiosError.message}`,
            axiosError,
        )

        throw new Error(axiosError.message)
    }
}

export interface UpdateArchivedStatusResponse {
    detail: 'success' | 'error'
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    data?: any
}

export async function updateApplicationArchivedStatus(
    appId: number,
    isArchived: boolean,
): Promise<UpdateArchivedStatusResponse> {
    try {
        const { data } = await axios.patch(
            `${config.API_BASE_URL}/api/v3/applications/${appId}/`,
            {
                archived: isArchived,
            },
            {
                withCredentials: true,
                headers: defaultLosJsonHeaders(),
            },
        )

        return { detail: 'success', data }
    } catch (error) {
        const axiosError = error as AxiosError

        OctaneLogger.axiosError(
            `Failed to update application archived status: ${axiosError.message}`,
            axiosError,
        )

        return { detail: 'error' }
    }
}
